import { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Switch, Space, Typography, message, Tag, Popconfirm, DatePicker, InputNumber, Row, Col, Card } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SolutionOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { careerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { TextArea } = Input;

export default function CareersPage() {
    const [careers, setCareers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCareer, setEditingCareer] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchCareers();
    }, []);

    const fetchCareers = async () => {
        try {
            const response = await careerAPI.getAll();
            const data = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
            setCareers(data);
        } catch (error) {
            message.error('Failed to fetch careers');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        form.resetFields();
        setEditingCareer(null);
        setShowModal(true);
    };

    const handleEdit = (career) => {
        setEditingCareer(career);
        form.setFieldsValue({
            ...career,
            applicationDeadline: career.applicationDeadline ? dayjs(career.applicationDeadline) : null,
            'salaryRange.min': career.salaryRange?.min || '',
            'salaryRange.max': career.salaryRange?.max || '',
            'salaryRange.currency': career.salaryRange?.currency || 'IDR',
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        try {
            await careerAPI.delete(id);
            message.success('Career deleted successfully!');
            fetchCareers();
        } catch (error) {
            message.error('Error deleting career');
        }
    };

    const handleSubmit = async (values) => {
        try {
            const submitData = {
                ...values,
                applicationDeadline: values.applicationDeadline ? values.applicationDeadline.toISOString() : null,
                salaryRange: {
                    min: values['salaryRange.min'] ? Number(values['salaryRange.min']) : undefined,
                    max: values['salaryRange.max'] ? Number(values['salaryRange.max']) : undefined,
                    currency: values['salaryRange.currency'] || 'IDR'
                }
            };

            // Remove nested field notations
            delete submitData['salaryRange.min'];
            delete submitData['salaryRange.max'];
            delete submitData['salaryRange.currency'];

            if (editingCareer) {
                await careerAPI.update(editingCareer.id || editingCareer._id, submitData);
                message.success('Career updated successfully!');
            } else {
                await careerAPI.create(submitData);
                message.success('Career created successfully!');
            }
            fetchCareers();
            setShowModal(false);
            form.resetFields();
        } catch (error) {
            message.error('Error: ' + (error.response?.data?.message || error.message));
        }
    };

    const columns = [
        {
            title: 'Position',
            dataIndex: 'title',
            key: 'title',
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag color={type === 'Full Time' ? 'blue' : type === 'Part Time' ? 'green' : 'orange'}>
                    {type}
                </Tag>
            ),
        },
        {
            title: 'Location',
            dataIndex: 'location',
            key: 'location',
        },
        {
            title: 'Department',
            dataIndex: 'department',
            key: 'department',
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <Space>
                    <Tag color={record.isActive ? 'green' : 'red'}>
                        {record.isActive ? 'Active' : 'Inactive'}
                    </Tag>
                    {record.isFeatured && <Tag color="gold">Featured</Tag>}
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small">
                        Edit
                    </Button>
                    <Popconfirm title="Delete this career?" onConfirm={() => handleDelete(record.id || record._id)}>
                        <Button danger icon={<DeleteOutlined />} size="small">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={2} style={{ margin: 0 }}><SolutionOutlined /> Careers</Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} size="large" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
                    Add Career
                </Button>
            </div>

            <Table columns={columns} dataSource={careers} rowKey={(record) => record.id || record._id} loading={loading} pagination={{ pageSize: 10 }} />

            <Modal
                title={editingCareer ? 'Edit Career' : 'Add Career'}
                open={showModal}
                onCancel={() => { setShowModal(false); form.resetFields(); }}
                footer={null}
                width={900}
                centered
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ type: 'Full Time', isActive: true, isFeatured: false, responsibilities: [''], requirements: [''], qualifications: [''], benefits: [''], 'salaryRange.currency': 'IDR' }}>
                    <Card size="small" title="Basic Information" style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="title" label="Position Title" rules={[{ required: true }]}>
                                    <Input size="large" placeholder="e.g., Senior Engineer" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="slug" label="Slug" rules={[{ required: true }]}>
                                    <Input size="large" placeholder="senior-engineer" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="type" label="Employment Type" rules={[{ required: true }]}>
                                    <Select size="large">
                                        <Select.Option value="Full Time">Full Time</Select.Option>
                                        <Select.Option value="Part Time">Part Time</Select.Option>
                                        <Select.Option value="Contract">Contract</Select.Option>
                                        <Select.Option value="Internship">Internship</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="location" label="Location" rules={[{ required: true }]}>
                                    <Input size="large" placeholder="Jakarta" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="department" label="Department">
                                    <Input size="large" placeholder="Engineering" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="description" label="Job Description" rules={[{ required: true }]}>
                            <TextArea rows={4} placeholder="Describe the job position..." />
                        </Form.Item>
                    </Card>

                    <Card size="small" title="Responsibilities" style={{ marginBottom: 16 }}>
                        <Form.List name="responsibilities">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map((field, index) => (
                                        <Space key={field.key} style={{ display: 'flex', marginBottom: 8, width: '100%' }} align="baseline">
                                            <Form.Item {...field} style={{ marginBottom: 0, flex: 1 }}>
                                                <Input size="large" placeholder={`Responsibility ${index + 1}`} />
                                            </Form.Item>
                                            {fields.length > 1 && <Button danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />}
                                        </Space>
                                    ))}
                                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>Add Responsibility</Button>
                                </>
                            )}
                        </Form.List>
                    </Card>

                    <Card size="small" title="Requirements" style={{ marginBottom: 16 }}>
                        <Form.List name="requirements">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map((field, index) => (
                                        <Space key={field.key} style={{ display: 'flex', marginBottom: 8, width: '100%' }} align="baseline">
                                            <Form.Item {...field} style={{ marginBottom: 0, flex: 1 }}>
                                                <Input size="large" placeholder={`Requirement ${index + 1}`} />
                                            </Form.Item>
                                            {fields.length > 1 && <Button danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />}
                                        </Space>
                                    ))}
                                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>Add Requirement</Button>
                                </>
                            )}
                        </Form.List>
                    </Card>

                    <Card size="small" title="Qualifications" style={{ marginBottom: 16 }}>
                        <Form.List name="qualifications">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map((field, index) => (
                                        <Space key={field.key} style={{ display: 'flex', marginBottom: 8, width: '100%' }} align="baseline">
                                            <Form.Item {...field} style={{ marginBottom: 0, flex: 1 }}>
                                                <Input size="large" placeholder={`Qualification ${index + 1}`} />
                                            </Form.Item>
                                            {fields.length > 1 && <Button danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />}
                                        </Space>
                                    ))}
                                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>Add Qualification</Button>
                                </>
                            )}
                        </Form.List>
                    </Card>

                    <Card size="small" title="Benefits" style={{ marginBottom: 16 }}>
                        <Form.List name="benefits">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map((field, index) => (
                                        <Space key={field.key} style={{ display: 'flex', marginBottom: 8, width: '100%' }} align="baseline">
                                            <Form.Item {...field} style={{ marginBottom: 0, flex: 1 }}>
                                                <Input size="large" placeholder={`Benefit ${index + 1}`} />
                                            </Form.Item>
                                            {fields.length > 1 && <Button danger icon={<MinusCircleOutlined />} onClick={() => remove(field.name)} />}
                                        </Space>
                                    ))}
                                    <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>Add Benefit</Button>
                                </>
                            )}
                        </Form.List>
                    </Card>

                    <Card size="small" title="Additional Information" style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="salaryRange.min" label="Min Salary">
                                    <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="5000000" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="salaryRange.max" label="Max Salary">
                                    <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="10000000" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="salaryRange.currency" label="Currency">
                                    <Select size="large">
                                        <Select.Option value="IDR">IDR</Select.Option>
                                        <Select.Option value="USD">USD</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item name="applicationDeadline" label="Application Deadline">
                            <DatePicker size="large" style={{ width: '100%' }} />
                        </Form.Item>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="contactEmail" label="Contact Email">
                                    <Input size="large" type="email" placeholder="hr@company.com" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="contactPhone" label="Contact Phone">
                                    <Input size="large" placeholder="+62 812 3456 7890" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Space>
                            <Form.Item name="isActive" label="Active" valuePropName="checked" style={{ marginBottom: 0 }}>
                                <Switch />
                            </Form.Item>
                            <Form.Item name="isFeatured" label="Featured" valuePropName="checked" style={{ marginBottom: 0 }}>
                                <Switch />
                            </Form.Item>
                        </Space>
                    </Card>

                    <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button size="large" onClick={() => { setShowModal(false); form.resetFields(); }}>Cancel</Button>
                            <Button type="primary" size="large" htmlType="submit" style={{ minWidth: 120, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
                                {editingCareer ? 'Update' : 'Create'} Career
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
